<template>
    <div class="footer-store-list">
        <div
            v-for="store in stores"
            :key="store.name"
            class="footer-store-item"
            @mouseenter="onStoreMouseEnter(store.name)"
            @mouseleave="onStoreMouseLeave"
        >
            <div class="footer-store-item__info">
                <a
                    :href="store.url"
                    class="footer-store-link"
                    @click="onStoreLinkClick(store, $event)"
                >
                    <svg-icon
                        icon-name="location-regular"
                        class="footer-store-link__icon"
                    ></svg-icon>
                    <span>{{ store.name }}</span>
                </a>
                <div class="footer-store-phone">{{ store.phone }}</div>
            </div>

            <div class="footer-store-qr-wrapper">
                <div class="footer-store-qr-wrapper__image">
                    <a
                        :href="store.qrCodeUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            :src="store.qrImageSrc"
                            :alt="`${store.name}_LINE`"
                        />
                    </a>
                </div>

                <!-- qr popup -->
                <div
                    class="footer-store-qr-wrapper__popup"
                    :class="{ 'is-visible': hoveredStoreName === store.name }"
                >
                    <article class="store-tooltip-panel">
                        <div class="store-tooltip-panel__inner">
                            <div class="store-tooltip-panel__qr">
                                <a
                                    :href="store.qrCodeUrl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="store-tooltip-panel__qr-link"
                                >
                                    <img
                                        :src="store.qrImageSrc"
                                        :alt="`${store.name}_LINE`"
                                    />
                                </a>
                            </div>
                            <h6 class="store-tooltip-panel__title">
                                門店資訊 - {{ store.name }}
                            </h6>
                            <div class="store-tooltip-panel__manager">
                                <img
                                    :src="store.managerImageSrc"
                                    :alt="store.managerName"
                                    class="store-tooltip-panel__manager-photo"
                                />
                                <div class="store-tooltip-panel__manager-name">
                                    <div>
                                        {{ store.managerName }}
                                        {{ store.managerTitle }}
                                    </div>
                                    <div>電話：{{ store.managerPhone }}</div>
                                </div>
                            </div>
                        </div>

                        <div class="store-tooltip-panel__msg">
                            <div
                                v-for="item in storeInfoItems"
                                :key="item.key"
                                class="store-tooltip-panel__msg-row"
                                :class="`store-tooltip-panel__msg-row--${item.key}`"
                            >
                                <svg-icon
                                    :icon-name="item.iconName"
                                    class="store-tooltip-panel__msg-icon"
                                ></svg-icon>
                                <span
                                    :class="{
                                        'store-tooltip-panel__hours':
                                            item.key === 'time',
                                    }"
                                >
                                    {{ item.label }}：{{ item.getValue(store) }}
                                </span>
                            </div>
                        </div>
                    </article>
                </div>
            </div>
        </div>

        <!-- qr modal -->
        <gh-modal
            ref="storeModalRef"
            class="footer-store-modal"
            @closed="onStoreModalClosed"
        >
            <article
                v-if="selectedMobileStore"
                class="store-tooltip-panel store-tooltip-panel--modal"
            >
                <div class="store-tooltip-panel__inner">
                    <div class="store-tooltip-panel__qr">
                        <a
                            :href="selectedMobileStore.qrCodeUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="store-tooltip-panel__qr-link"
                        >
                            <img
                                :src="selectedMobileStore.qrImageSrc"
                                :alt="`${selectedMobileStore.name}_LINE`"
                            />
                        </a>
                    </div>
                    <h6 class="store-tooltip-panel__title">
                        門店資訊 - {{ selectedMobileStore.name }}
                    </h6>
                    <div class="store-tooltip-panel__manager">
                        <img
                            :src="selectedMobileStore.managerImageSrc"
                            :alt="selectedMobileStore.managerName"
                            class="store-tooltip-panel__manager-photo"
                        />
                        <div class="store-tooltip-panel__manager-name">
                            <div>
                                {{ selectedMobileStore.managerName }}
                                {{ selectedMobileStore.managerTitle }}
                            </div>
                            <div>
                                電話：{{ selectedMobileStore.managerPhone }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="store-tooltip-panel__msg">
                    <div
                        v-for="item in storeInfoItems"
                        :key="item.key"
                        class="store-tooltip-panel__msg-row"
                        :class="`store-tooltip-panel__msg-row--${item.key}`"
                    >
                        <svg-icon
                            :icon-name="item.iconName"
                            class="store-tooltip-panel__msg-icon"
                        ></svg-icon>
                        <span
                            :class="{
                                'store-tooltip-panel__hours':
                                    item.key === 'time',
                            }"
                        >
                            {{ item.label }}：{{
                                item.getValue(selectedMobileStore)
                            }}
                        </span>
                    </div>
                </div>

                <div class="store-tooltip-panel__actions">
                    <a
                        :href="selectedMobileStore.url"
                        class="store-tooltip-panel__more-link"
                    >
                        更詳細門市資訊
                    </a>
                </div>
            </article>
        </gh-modal>
    </div>
</template>

<script setup lang="ts">
    import { ref } from 'vue';
    import GhModal from '@/components/base/GhModal.vue';
    import SvgIcon from '@/components/base/SvgIcon.vue';
    import type { LayoutFooterStore } from '@/components/layout/constants/LayoutFooter.constants';

    interface StoreInfoItem {
        key: 'phone' | 'place' | 'time' | 'service';
        iconName: string;
        label: string;
        getValue: (store: LayoutFooterStore) => string;
    }

    // ----- props -----
    defineProps<{
        stores: LayoutFooterStore[];
    }>();

    // ----- state -----
    const storeInfoItems: StoreInfoItem[] = [
        {
            key: 'phone',
            iconName: 'phone-regular',
            label: '電話',
            getValue: (store) => store.fullPhone,
        },
        {
            key: 'place',
            iconName: 'location-regular',
            label: '地址',
            getValue: (store) => store.address,
        },
        {
            key: 'time',
            iconName: 'clock-regular',
            label: '門市服務時間',
            getValue: (store) => store.hours,
        },
        {
            key: 'service',
            iconName: 'network-regular',
            label: '服務項目',
            getValue: (store) => store.services,
        },
    ];
    const hoveredStoreName = ref<string | null>(null);
    const selectedMobileStore = ref<LayoutFooterStore | null>(null);
    const storeModalRef = ref<InstanceType<typeof GhModal> | null>(null);
    const mobileQuery = window.matchMedia('(max-width: 992px)');

    // ----- methods -----
    // 行動版點擊門市連結時，改以 modal 顯示門店資訊，避免直接跳轉離開 footer
    const onStoreLinkClick = (
        store: LayoutFooterStore,
        event: MouseEvent
    ): void => {
        if (!mobileQuery.matches) return;

        event.preventDefault();
        selectedMobileStore.value = store;
        storeModalRef.value?.open({
            title: '',
            showReject: false,
            width: '360px',
        });
    };

    const onStoreMouseEnter = (name: string): void => {
        if (mobileQuery.matches) return;

        hoveredStoreName.value = name;
    };

    const onStoreMouseLeave = (): void => {
        hoveredStoreName.value = null;
    };

    const onStoreModalClosed = (): void => {
        selectedMobileStore.value = null;
    };
</script>

<style scoped lang="scss">
    .footer-store-list {
        padding-top: 3px;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 0.5rem 1.5rem;
    }

    .footer-store-item {
        position: relative;
        flex: 0 0 calc((100% - 1.5rem * 2) / 3);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-items: center;
        line-height: 1.5;

        @media (min-width: 993px) and (max-width: 1200px) {
            flex: 0 0 calc((100% - 1.5rem * 1) / 2);
        }

        @media (max-width: 440px) {
            flex: 0 0 calc((100% - 1.5rem * 1) / 2);
        }

        &__info {
            display: flex;
            flex-direction: column;
            align-items: center;
        }
    }

    .footer-store-link {
        display: inline-flex;
        align-items: center;
        font-size: 1rem;
        line-height: 1.5;

        &__icon {
            width: 1.5rem;
            height: 1.5rem;
            flex: 0 0 auto;
            margin-top: -3px;
            display: block !important;
        }
    }

    .footer-store-phone {
        font-size: 1rem;
        line-height: 1.5;
    }

    .footer-store-qr-wrapper {
        margin-top: 0.25rem;
        display: inline-flex;
        flex-direction: column;
        align-items: center;

        &__image {
            width: 100px;
            height: 100px;
            border-radius: 8px;
            overflow: hidden;

            a,
            img {
                width: 100%;
                height: 100%;
                display: block;
            }
        }

        &__popup {
            position: absolute;
            bottom: 100%;
            right: 0;
            padding-bottom: 10px;
            opacity: 0;
            visibility: hidden;
            transform: translateY(10px);
            pointer-events: none;
            z-index: $z-index-popover;
            transition:
                opacity 0.3s,
                transform 0.3s,
                visibility 0.3s;

            &.is-visible {
                opacity: 1;
                visibility: visible;
                transform: translateY(0);
                pointer-events: auto;
            }
        }
    }

    .store-tooltip-panel {
        width: 325px;
        padding: 1rem;
        background-color: #ffffff;
        color: $textColor888;
        font-size: 0.88rem;
        text-align: start;
        border-radius: 8px;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            right: 1rem;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 1rem 1rem 0 1rem;
            border-color: #ffffff transparent transparent transparent;
        }

        &--modal {
            width: 100%;
            box-shadow: none;

            &::after {
                display: none;
            }
        }

        &__inner {
            padding-bottom: 0.75rem;
            margin-bottom: 0.75rem;
            border-bottom: 1px solid $grayColorE7;
            overflow: hidden;
        }

        &__qr {
            float: right;
            width: 64px;
            height: 64px;
            margin-left: 8px;
            border-radius: 4px;
            overflow: hidden;

            a,
            img {
                width: 100%;
                height: 100%;
                display: block;
            }
        }

        &__qr-link {
            display: block;
            color: inherit;
            text-decoration: none;
        }

        &__title {
            margin: 0 0 0.5rem;
            padding: 0;
            color: #000000;
            font-size: 1rem;
            font-weight: 700;
            pointer-events: none;
        }

        &__manager {
            display: flex;
            align-items: center;
            color: $textColor45;
        }

        &__manager-photo {
            flex-shrink: 0;
            width: 2rem;
            height: 2rem;
            margin-right: 0.5rem;
            border-radius: 50%;
            object-fit: cover;
        }

        &__manager-name {
            font-size: 0.88rem;
            line-height: 1.5;
        }

        &__msg {
            display: flex;
            flex-direction: column;
            gap: 0.3rem;
        }

        &__msg-row {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
            line-height: 1.5;

            span {
                flex: 1 1 auto;
            }
        }

        &__msg-icon {
            flex: 0 0 auto;
            width: 14px;
            height: 14px;
            margin-top: 3px;
            color: $textColor888;
        }

        &__hours {
            white-space: pre-line;
        }

        &__actions {
            margin-top: 1rem;
        }

        &__more-link {
            padding: 8px 0;
            display: block;
            color: #4e99fc;
            font-size: 15px;
            text-align: center;
            border: 1px solid #4e99fc;
            border-radius: 4px;
            text-decoration: none;
        }
    }

    .footer-store-modal:deep(.gh-modal-container) {
        .gh-modal-header {
            padding: 12px 12px 0;
        }
        .gh-modal-body {
            padding: 0;
        }
        .gh-modal-body-scroll {
            padding-right: 0;
            white-space: normal;
        }
    }

    @include rwd($pc) {
        .footer-store-list {
            padding: 0 24px 16px;
        }
    }
</style>
